import React, { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Section, Dropdown, TextField, NumberField, Button, SmSpace, AutoComplete } from 'm2m.components';
import { Grid, Chip, Checkbox, FormLabel, styled } from '@material-ui/core';
import { Add, Remove } from '@material-ui/icons';
import { CpfCnpjMask, TelefoneMask, CepMask } from 'components/masks';
import { RootState } from 'store';
import { BancoResponse } from 'common/genericTypes';
import { updateField, updateEndereco, updateContato } from 'store/modules/participantes/registro';
import { unidadeFederativaOptions, tipoAcessoOptions, funcaoParticipanteOptions } from 'common/genericOptions';
import { validaCpfCnpj, validaCep } from 'utils/validation';
import { useCallback } from 'react';
import { FuncaoParticipante } from 'common/genericTypes';

interface InfoPrincipaisProps {
  blockCategoria?: boolean;
  isStepFulfilled(isFulfilled: boolean): void;
}

const mapStateToProps = (state: RootState) => ({
  participante: state.participante,
});

const mapDispatchToProps = { updateField, updateEndereco, updateContato };

type Props = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps & InfoPrincipaisProps;

const InfoPrincipais: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const {
    isStepFulfilled,
    updateField,
    updateEndereco,
    updateContato,
    participante: {
      tipoAcesso,
      atividadePrincipal,
      funcoes,
      cnpjParticipante,
      faturamento,
      razaoSocial,
      contato,
      endereco,
      razaoSocialBancoLiquidante,
      ispbBancoLiquidante,
      inscricaoEstadual,
      inscricaoMunicipal,
      grupoEconomico
    },
  } = props;

  const [infoPrincipaisValido, setInfoPrincipaisValido] = useState<boolean>(false);
  const [enderecoValido, setEnderecoValido] = useState<boolean>(false);

  const [bancos, setBancos] = useState<Array<BancoResponse>>([]);
  const [banco, setBanco] = useState<BancoResponse | null>(null);

  const [telefone, setTelefone] = useState<string>('');

  const getEnderecoByCepApi = useSelector((state: RootState) => state.catalogoApi.getEndereco);
  const getBancosApi = useSelector((state: RootState) => state.catalogoApi.getBancos);
  const usuario = useSelector((state: RootState) => state.auth.usuario);

  const autocompleteEndereco = useCallback(
    async (cep: string) => {
      try {
        if (cep?.length !== 9) {
          return;
        }
        const result = await getEnderecoByCepApi(dispatch, cep);
        if (result) {
          updateEndereco({ name: 'logradouro', value: result?.logradouro });
          updateEndereco({ name: 'bairro', value: result?.bairro });
          updateEndereco({ name: 'cidade', value: result?.localidade });
          updateEndereco({ name: 'uf', value: result?.uf });
        }
      } catch {}
    },
    [dispatch, getEnderecoByCepApi, updateEndereco]
  );

  const getBancos = useCallback(async () => {
    try {
      const result = await getBancosApi(dispatch, true);
      setBancos(result.banks);
      if (razaoSocialBancoLiquidante && ispbBancoLiquidante) {
        const bancoAntigo = result.banks.find(
          (_) => _.ispb === ispbBancoLiquidante && _.shortname === razaoSocialBancoLiquidante
        );
        setBanco(bancoAntigo ?? null);
      }
    } catch (error) {}
  }, [dispatch, getBancosApi, razaoSocialBancoLiquidante, ispbBancoLiquidante]);

  const bancosFilter = (value: string) =>
    bancos.filter(
      (banco) =>
        banco.code.includes(value) ||
        banco.fullname.toLocaleLowerCase().includes(value) ||
        banco.ispb.includes(value) ||
        banco.shortname.toLocaleLowerCase().includes(value)
    );

  const handleAddTelefone = () => {
    var telMatch = telefone.match(/\d+/g);
    if (!telefone || !telMatch || telMatch.join('').length < 10) return;

    const newTelefones = Array.from(new Set([...contato.telefones, telefone]));
    updateContato({ name: 'telefones', value: newTelefones });
    setTelefone('');
  };

  const handleRemoveTelefone = (telefone: string) => {
    const newTelefones = contato.telefones.filter((tel) => tel !== telefone);
    updateContato({ name: 'telefones', value: newTelefones });
  };

  useEffect(() => {
    isStepFulfilled(
      infoPrincipaisValido &&
        enderecoValido &&
        contato.telefones.length > 0 &&
        funcoes.length > 0 &&
        !!banco &&
        validaCpfCnpj(cnpjParticipante) &&
        validaCep(endereco.cep)
    );
  }, [infoPrincipaisValido, enderecoValido, cnpjParticipante, contato, endereco, funcoes, banco, isStepFulfilled]);

  useEffect(() => {
    getBancos();
  }, [getBancos]);

  useEffect(() => {
    updateField({ name: 'razaoSocialBancoLiquidante', value: banco?.shortname ?? '' });
    updateField({ name: 'ispbBancoLiquidante', value: banco?.ispb ?? '' });
  }, [banco]);

  const CustomCheckbox = styled(Checkbox)(({ theme }) => ({
    color: theme.palette.grey[500],
    '&.Mui-checked': {
      color: theme.palette.success.main,
    },
  }));

  return (
    <>
      <Section
        title={'Informações Principais'}
        lgFullWidth
        defaultGrid
        isFulfilled={(fulfilled) => setInfoPrincipaisValido(fulfilled)}
      >
        <TextField
          label="CNPJ Participante"
          required
          disabled
          value={CpfCnpjMask(cnpjParticipante)}
          onChange={(value) => updateField({ name: 'cnpjParticipante', value })}
        />
        <Dropdown
          label={'Tipo de Acesso'}
          required
          options={tipoAcessoOptions}
          value={tipoAcesso}
          onChange={(value) => updateField({ name: 'tipoAcesso', value })}
        />
        <TextField
          label="Denominação ou razão social"
          required
          value={razaoSocial}
          onChange={(value) => updateField({ name: 'razaoSocial', value })}
        />
        <NumberField
          label="Faturamento médio mensal"
          value={faturamento}
          onChange={(value) => updateField({ name: 'faturamento', value: value === undefined ? null : value })}
          helperText="Últimos 12 meses"
          numberType="money"
        />
        <TextField
          label="Atividade principal desenvolvida"
          required
          value={atividadePrincipal}
          onChange={(value) => updateField({ name: 'atividadePrincipal', value })}
        />
        <TextField
          label="Inscrição Estadual"
          value={props.participante.inscricaoEstadual}
          onChange={(value) => updateField({ name: 'inscricaoEstadual', value })}
        />
        <TextField
          label="Inscrição Municipal"
          value={props.participante.inscricaoMunicipal}
          onChange={(value) => updateField({ name: 'inscricaoMunicipal', value })}
        />
        <TextField
          label="Grupo Econômico"
          value={props.participante.grupoEconomico}
          onChange={(value) => updateField({ name: 'grupoEconomico', value })}
        />
        {tipoAcesso === 'PARTICIPANTE' && (
          <AutoComplete
            multiple
            required
            options={funcaoParticipanteOptions.filter((op) => {
              if (usuario.participante.funcao == FuncaoParticipante.Custodiante && usuario.id) return op.value === FuncaoParticipante.Emissor;
              return !funcoes.some((f) => f.value === op.value);
            })}
            getOptionLabel={(option) => option.description}
            value={funcoes}
            onChange={(newValue) => updateField({ name: 'funcoes', value: [...newValue] })}
            label={'Funções'}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => <Chip label={option.description} {...getTagProps({ index })} />)
            }
          />
        )}
      </Section>
      <Section title={'Dados Bancários'} defaultGrid>
        <AutoComplete
          label={'Banco Liquidante'}
          options={bancos}
          getOptionLabel={(option: BancoResponse) => `${option.code} - ${option.shortname}`}
          value={banco}
          onChange={(newValue) => setBanco(newValue)}
          filterOptions={(_options: Array<BancoResponse>, state) => bancosFilter(state.inputValue.toLocaleLowerCase())}
        />
      </Section>
      <Section title={'Endereço e Contato'} defaultGrid isFulfilled={(fulfilled) => setEnderecoValido(fulfilled)}>
        <TextField
          label="CEP"
          required
          value={CepMask(endereco.cep)}
          onChange={(value) => updateEndereco({ name: 'cep', value })}
          onBlur={(event) => autocompleteEndereco(event.target.value)}
        />
        <TextField
          label="Logradouro"
          required
          value={endereco.logradouro}
          onChange={(value) => updateEndereco({ name: 'logradouro', value })}
        />
        <TextField
          label="Número"
          required
          value={endereco.numero}
          onChange={(value) => updateEndereco({ name: 'numero', value })}
        />
        <TextField
          label="Complemento"
          value={endereco.complemento}
          onChange={(value) => updateEndereco({ name: 'complemento', value })}
        />
        <TextField
          label="Bairro"
          required
          value={endereco.bairro}
          onChange={(value) => updateEndereco({ name: 'bairro', value })}
        />
        <TextField
          label="Cidade"
          required
          value={endereco.cidade}
          onChange={(value) => updateEndereco({ name: 'cidade', value })}
        />
        <Dropdown
          label={'Unidade da Federação'}
          required
          options={unidadeFederativaOptions}
          value={endereco.uf}
          onChange={(value) => updateEndereco({ name: 'uf', value })}
        />
        <Grid container spacing={2} style={{ width: '100%' }}>
          <Grid item xs={12} md={12}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs>
                <TextField
                  label="Telefone"
                  required
                  value={TelefoneMask(telefone)}
                  onChange={(value) => setTelefone(value)}
                />
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  style={{ minWidth: '0', padding: '5px', width: '45px', height: '45px' }}
                  onClick={handleAddTelefone}
                >
                  <Add />
                </Button>
              </Grid>
            </Grid>
            {contato.telefones.map((telefone, index) => (
              <Grid container spacing={2} alignItems="center" key={index}>
                <Grid item xs>
                  <SmSpace />
                  <TextField disabled label={'Telefone'} value={TelefoneMask(telefone)} onChange={() => null} />
                </Grid>
                <Grid item>
                  <SmSpace />
                  <Button
                    variant="contained"
                    color="error"
                    style={{ minWidth: '0', padding: '5px', width: '45px', height: '45px' }}
                    onClick={() => handleRemoveTelefone(telefone)}
                  >
                    <Remove />
                  </Button>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Section>
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(InfoPrincipais);